/**
 * HealthCheckTab Component
 * 
 * Exibe o Health Check automático do sistema com verificações ao vivo
 */

import { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, Button, ProgressBar, Spinner, Alert, Table } from 'react-bootstrap';

const HealthCheckTab = () => {
    const [healthData, setHealthData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isRunning, setIsRunning] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentCheck, setCurrentCheck] = useState('');

    // Database Guardian
    const [guardianStatus, setGuardianStatus] = useState(null);
    const [guardianLoading, setGuardianLoading] = useState(false);

    // Carregar último relatório ao montar
    useEffect(() => {
        loadLastReport();
        loadGuardianStatus();
    }, []);

    const loadGuardianStatus = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/health/guardian/status', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setGuardianStatus(data);
            }
        } catch (err) {
            console.error('Erro ao carregar status do Guardian:', err);
        }
    };

    const runGuardianCheck = async () => {
        setGuardianLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/health/guardian/check', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success !== undefined) {
                await loadGuardianStatus();
            }
        } catch (err) {
            console.error('Erro ao executar Guardian check:', err);
        } finally {
            setGuardianLoading(false);
        }
    };

    const loadLastReport = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/health/report', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success && data.report) {
                setHealthData(data.report);
            }
        } catch (err) {
            console.error('Erro ao carregar relatório:', err);
        }
    };

    const runHealthCheck = async () => {
        setIsRunning(true);
        setLoading(true);
        setError(null);
        setProgress(0);

        const steps = [
            { progress: 15, label: 'Verificando integridade SQLite...' },
            { progress: 30, label: 'Verificando colunas obrigatórias...' },
            { progress: 45, label: 'Verificando dados de vendas...' },
            { progress: 60, label: 'Verificando backups...' },
            { progress: 75, label: 'Verificando conexões MySQL...' },
            { progress: 90, label: 'Executando auto-reparos...' },
            { progress: 100, label: 'Finalizando...' }
        ];

        // Simular progresso visual
        let stepIndex = 0;
        const progressInterval = setInterval(() => {
            if (stepIndex < steps.length) {
                setProgress(steps[stepIndex].progress);
                setCurrentCheck(steps[stepIndex].label);
                stepIndex++;
            }
        }, 800);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/health/auto-check', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            clearInterval(progressInterval);
            setProgress(100);
            setCurrentCheck('Concluído!');

            if (data.success !== false) {
                setHealthData(data);
            } else {
                setError(data.error || 'Erro desconhecido');
            }
        } catch (err) {
            clearInterval(progressInterval);
            setError(err.message);
        } finally {
            setLoading(false);
            setTimeout(() => setIsRunning(false), 1000);
        }
    };

    const quickCheck = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/health/quick-check', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            return data;
        } catch (err) {
            return { status: 'error', error: err.message };
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            'ok': <Badge bg="success">✓ OK</Badge>,
            'healthy': <Badge bg="success">✅ Saudável</Badge>,
            'warning': <Badge bg="warning">⚠️ Atenção</Badge>,
            'error': <Badge bg="danger">❌ Erro</Badge>,
            'running': <Badge bg="info">🔄 Executando</Badge>,
            'pending': <Badge bg="secondary">⏳ Pendente</Badge>
        };
        return badges[status] || <Badge bg="secondary">{status}</Badge>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('pt-BR');
    };

    return (
        <>
            {/* Header com Ações */}
            <Row className="mb-4">
                <Col>
                    <Card className="border-0 shadow-sm">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h4 className="mb-1">🔍 Health Check do Sistema</h4>
                                    <p className="text-muted mb-0">
                                        Verificação automática de integridade, conexões e dados
                                    </p>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="outline-primary"
                                        onClick={loadLastReport}
                                        disabled={loading}
                                    >
                                        🔄 Atualizar
                                    </Button>
                                    <Button
                                        variant="success"
                                        onClick={runHealthCheck}
                                        disabled={loading}
                                        size="lg"
                                    >
                                        {loading ? (
                                            <><Spinner animation="border" size="sm" className="me-2" /> Executando...</>
                                        ) : (
                                            '▶️ Executar Health Check'
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* 🛡️ DATABASE GUARDIAN - PAINEL DE PROTEÇÃO */}
            <Row className="mb-4">
                <Col>
                    <Card className="border-primary" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
                        <Card.Header className="d-flex justify-content-between align-items-center" style={{ background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid #0d6efd' }}>
                            <div className="d-flex align-items-center">
                                <span style={{ fontSize: '1.5rem' }}>🛡️</span>
                                <h5 className="mb-0 ms-2 text-white">Database Guardian</h5>
                                <Badge bg="info" className="ms-2">Proteção Automática</Badge>
                            </div>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-light"
                                    size="sm"
                                    onClick={loadGuardianStatus}
                                    disabled={guardianLoading}
                                >
                                    🔄 Atualizar
                                </Button>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={runGuardianCheck}
                                    disabled={guardianLoading}
                                >
                                    {guardianLoading ? (
                                        <><Spinner animation="border" size="sm" className="me-1" /> Verificando...</>
                                    ) : (
                                        '▶️ Verificar Integridade'
                                    )}
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {guardianStatus ? (
                                <>
                                    <Row className="mb-3">
                                        <Col md={3}>
                                            <div className="text-center p-3" style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                                                <h1 className="mb-0" style={{ fontSize: '2.5rem' }}>
                                                    {guardianStatus.isRunning ? '✅' : '⏹️'}
                                                </h1>
                                                <div className="text-white mt-2">
                                                    {guardianStatus.isRunning ? 'Ativo' : 'Inativo'}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <div className="text-center p-3" style={{ background: 'rgba(40,167,69,0.2)', borderRadius: '8px' }}>
                                                <h2 className="text-success mb-0">
                                                    {guardianStatus.lastCheck?.summary?.ok || 0}
                                                </h2>
                                                <div className="text-white-50">Bancos OK</div>
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <div className="text-center p-3" style={{ background: 'rgba(23,162,184,0.2)', borderRadius: '8px' }}>
                                                <h2 className="text-info mb-0">
                                                    {guardianStatus.lastCheck?.summary?.recovered || 0}
                                                </h2>
                                                <div className="text-white-50">Recuperados</div>
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <div className="text-center p-3" style={{ background: guardianStatus.lastCheck?.summary?.problems > 0 ? 'rgba(220,53,69,0.3)' : 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                                                <h2 className={guardianStatus.lastCheck?.summary?.problems > 0 ? 'text-danger' : 'text-white'} style={{ marginBottom: 0 }}>
                                                    {guardianStatus.lastCheck?.summary?.problems || 0}
                                                </h2>
                                                <div className="text-white-50">Problemas</div>
                                            </div>
                                        </Col>
                                    </Row>

                                    {/* Bancos Monitorados */}
                                    <div className="mb-3">
                                        <h6 className="text-white-50 mb-2">📊 Bancos Monitorados</h6>
                                        <div className="d-flex flex-wrap gap-2">
                                            {(guardianStatus.lastCheck?.databases || []).map((db, i) => (
                                                <Badge
                                                    key={i}
                                                    bg={db.status === 'OK' ? 'success' : db.status === 'RECOVERED' ? 'info' : 'danger'}
                                                    className="p-2"
                                                    style={{ fontSize: '0.85rem' }}
                                                >
                                                    {db.status === 'OK' ? '✓' : db.status === 'RECOVERED' ? '🔧' : '⚠️'} {db.name}
                                                    {db.details?.recordCheck?.count && (
                                                        <span className="ms-1 opacity-75">
                                                            ({db.details.recordCheck.count.toLocaleString()})
                                                        </span>
                                                    )}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Últimas Recuperações */}
                                    {guardianStatus.recoveryLog && guardianStatus.recoveryLog.length > 0 && (
                                        <div>
                                            <h6 className="text-white-50 mb-2">🔧 Últimas Recuperações</h6>
                                            <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                                {guardianStatus.recoveryLog.slice(-5).reverse().map((log, i) => (
                                                    <div key={i} className="d-flex align-items-center mb-1 p-2" style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '4px', fontSize: '0.85rem' }}>
                                                        <Badge bg={log.level === 'RECOVERY' ? 'info' : 'danger'} className="me-2">
                                                            {log.level === 'RECOVERY' ? '🔧' : '🚨'}
                                                        </Badge>
                                                        <span className="text-white-50 me-2">
                                                            {new Date(log.timestamp).toLocaleString('pt-BR')}
                                                        </span>
                                                        <span className="text-white">{log.message}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="text-white-50 mt-3" style={{ fontSize: '0.8rem' }}>
                                        🔄 Verificação automática a cada {guardianStatus.config?.checkIntervalMinutes || 5} minutos
                                        {guardianStatus.lastCheck?.timestamp && (
                                            <span className="ms-3">
                                                📅 Última verificação: {formatDate(guardianStatus.lastCheck.timestamp)}
                                            </span>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="text-center py-4">
                                    <Spinner animation="border" variant="light" />
                                    <p className="text-white-50 mt-2">Carregando status do Guardian...</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Progress Bar durante execução */}
            {isRunning && (
                <Row className="mb-4">
                    <Col>
                        <Card className="border-info">
                            <Card.Body>
                                <h6 className="mb-3">🔄 {currentCheck}</h6>
                                <ProgressBar
                                    animated
                                    now={progress}
                                    label={`${progress}%`}
                                    variant="info"
                                    style={{ height: '25px' }}
                                />
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Erro */}
            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    <strong>❌ Erro:</strong> {error}
                </Alert>
            )}

            {/* Resultados */}
            {healthData && (
                <>
                    {/* Status Geral */}
                    <Row className="mb-4">
                        <Col md={3}>
                            <Card className={`text-center h-100 ${healthData.status === 'healthy' ? 'border-success' : healthData.status === 'warning' ? 'border-warning' : 'border-danger'}`}>
                                <Card.Body>
                                    <h1 className="display-4">
                                        {healthData.status === 'healthy' ? '✅' : healthData.status === 'warning' ? '⚠️' : '❌'}
                                    </h1>
                                    <h5>{healthData.status === 'healthy' ? 'Sistema Saudável' : healthData.status === 'warning' ? 'Atenção Necessária' : 'Erro Crítico'}</h5>
                                    <small className="text-muted">
                                        Última verificação: {formatDate(healthData.timestamp)}
                                    </small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="text-center h-100 border-success">
                                <Card.Body>
                                    <h2 className="text-success">{healthData.summary?.passed || 0}</h2>
                                    <h6>Verificações OK</h6>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="text-center h-100 border-warning">
                                <Card.Body>
                                    <h2 className="text-warning">{healthData.summary?.warnings || 0}</h2>
                                    <h6>Avisos</h6>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="text-center h-100 border-danger">
                                <Card.Body>
                                    <h2 className="text-danger">{healthData.summary?.errors || 0}</h2>
                                    <h6>Erros</h6>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Detalhes das Verificações */}
                    <Row className="mb-4">
                        <Col md={8}>
                            <Card>
                                <Card.Header>📋 Detalhes das Verificações</Card.Header>
                                <Card.Body>
                                    <Table hover size="sm">
                                        <thead>
                                            <tr>
                                                <th>Tipo</th>
                                                <th>Banco/Recurso</th>
                                                <th>Status</th>
                                                <th>Detalhes</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(healthData.checks || []).map((check, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        {check.type === 'sqlite_integrity' && '🗄️ SQLite'}
                                                        {check.type === 'required_columns' && '📋 Colunas'}
                                                        {check.type === 'sales_data' && '📊 Dados'}
                                                        {check.type === 'backup_status' && '💾 Backup'}
                                                        {check.type === 'mysql' && '🔌 MySQL'}
                                                        {check.type === 'repair' && '🔧 Reparo'}
                                                        {!['sqlite_integrity', 'required_columns', 'sales_data', 'backup_status', 'mysql', 'repair'].includes(check.type) && check.type}
                                                    </td>
                                                    <td>
                                                        {check.label || check.database || check.table || check.action || '-'}
                                                    </td>
                                                    <td>{getStatusBadge(check.status)}</td>
                                                    <td>
                                                        {check.result && <small>{check.result}</small>}
                                                        {check.totalColumns && <small>{check.totalColumns} colunas</small>}
                                                        {check.totalProducts !== undefined && (
                                                            <small>
                                                                {check.totalProducts} produtos, {check.withSales} com vendas
                                                            </small>
                                                        )}
                                                        {check.lastSync && <small>Sync: {formatDate(check.lastSync)}</small>}
                                                        {check.missing && check.missing.length > 0 && (
                                                            <Badge bg="warning">{check.missing.length} faltando</Badge>
                                                        )}
                                                        {check.error && <small className="text-danger">{check.error}</small>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="mb-3">
                                <Card.Header>⏱️ Resumo</Card.Header>
                                <Card.Body>
                                    <p><strong>Duração:</strong> {healthData.summary?.duration || 'N/A'}</p>
                                    <p><strong>Total de Verificações:</strong> {healthData.summary?.totalChecks || 0}</p>
                                    <p><strong>Reparos Executados:</strong> {healthData.summary?.repairs || 0}</p>
                                    <hr />
                                    <p className="text-muted mb-0">
                                        <small>
                                            ⏰ Próxima verificação automática: 04:00
                                        </small>
                                    </p>
                                </Card.Body>
                            </Card>

                            {/* Erros Encontrados */}
                            {healthData.errors && healthData.errors.length > 0 && (
                                <Card className="border-danger">
                                    <Card.Header className="bg-danger text-white">❌ Erros Encontrados</Card.Header>
                                    <Card.Body>
                                        {healthData.errors.map((err, index) => (
                                            <Alert key={index} variant="danger" className="py-2 mb-2">
                                                <strong>{err.type}:</strong> {err.message}
                                                {err.database && <div><small>Banco: {err.database}</small></div>}
                                                {err.hoursSinceSync && <div><small>Última sync: {err.hoursSinceSync}h atrás</small></div>}
                                            </Alert>
                                        ))}
                                    </Card.Body>
                                </Card>
                            )}

                            {/* Reparos Pendentes */}
                            {healthData.repairs && healthData.repairs.length > 0 && (
                                <Card className="border-warning mt-3">
                                    <Card.Header className="bg-warning">🔧 Reparos Pendentes</Card.Header>
                                    <Card.Body>
                                        {healthData.repairs.map((repair, index) => (
                                            <div key={index} className="mb-2">
                                                <strong>{repair.type}:</strong> {repair.database}.{repair.table}
                                                {repair.columns && (
                                                    <div><small>Colunas: {repair.columns.join(', ')}</small></div>
                                                )}
                                            </div>
                                        ))}
                                    </Card.Body>
                                </Card>
                            )}
                        </Col>
                    </Row>

                    {/* Informações dos Bancos */}
                    <Row>
                        <Col>
                            <Card>
                                <Card.Header>🗄️ Status dos Bancos de Dados</Card.Header>
                                <Card.Body>
                                    <Row>
                                        {(healthData.checks || [])
                                            .filter(c => c.type === 'sales_data')
                                            .map((db, index) => (
                                                <Col md={6} key={index}>
                                                    <Card className="mb-3 border-0 bg-light">
                                                        <Card.Body>
                                                            <h6>
                                                                {db.database === 'loja_fisica' ? '🏪 Loja Física' : '🛒 E-commerce'}
                                                            </h6>
                                                            <Row>
                                                                <Col>
                                                                    <div className="text-muted">Total Produtos</div>
                                                                    <h4>{db.totalProducts?.toLocaleString() || 0}</h4>
                                                                </Col>
                                                                <Col>
                                                                    <div className="text-muted">Com Vendas</div>
                                                                    <h4 className="text-success">{db.withSales?.toLocaleString() || 0}</h4>
                                                                </Col>
                                                                <Col>
                                                                    <div className="text-muted">Última Sync</div>
                                                                    <small>{db.lastSync ? formatDate(db.lastSync) : 'Nunca'}</small>
                                                                </Col>
                                                            </Row>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            ))}
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}

            {/* Estado vazio */}
            {!healthData && !loading && (
                <Row>
                    <Col>
                        <Card className="text-center py-5">
                            <Card.Body>
                                <h1 className="display-1 text-muted">🔍</h1>
                                <h4 className="text-muted">Nenhum relatório disponível</h4>
                                <p className="text-muted">Clique em "Executar Health Check" para verificar o sistema</p>
                                <Button variant="primary" size="lg" onClick={runHealthCheck}>
                                    ▶️ Executar Agora
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
        </>
    );
};

export default HealthCheckTab;
